import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { t } from 'i18next'
import moment from 'moment-timezone'

import DatepickerHeader from '~/components/datepicker-header'
import EmptyPage from '~/components/empty-page'
import ListPagination from '~/components/ListPagination'
import Loading from '~/components/Loading'
import LoadingPopUp from '~/components/LoadingPopUp'
import Table from '~/components/Table'
import { formatSearchString } from '~/helpers'
import { useAlert } from '~/hooks/useAlert'
import { normalizeCounterpartId } from '~/modules/retailMedia/helpers/isValidCounterpartId'
import { reatailmediaApi } from '~/modules/retailMedia/services/api'
import { transactionStatus } from '~/modules/retailMedia/store/common/constants'
import { useAppSelector } from '~/store/hooks'

import {
  setPageIndustryListPayments,
  setPerPageIndustryListPayments
} from '../../store/industry/wallet/actions'
import activityStyles from '../WalletTableTransaction/walletActivitySection.module.css'
import { WalletShorelineFilter } from '../WalletTableTransaction/WalletShorelineFilter'
import { fetchAllPaginatedItems, filterWalletPayments } from './filterPayments'
import { schema } from './schema'

const PAYMENT_STATUS_OPTIONS = Object.keys(
  transactionStatus
) as WalletPaymentStatus[]

const PAYMENT_METHOD_OPTIONS: RetailMediaRechargePaymentMethods[] = [
  'credit_card',
  'pix',
  'marketplace'
]

export const IndustryPaymentsTable = () => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const publisherId = normalizeCounterpartId(
    new URLSearchParams(globalThis.location.search).get('rmid')
  )
  const [status, setStatus] = useState('')
  const [method, setMethod] = useState('')

  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)
  const {
    industry: {
      wallet: {
        listPayments: { page, perPage }
      }
    }
  } = useAppSelector(state => state.retailMedia)

  const { data, error, isFetching } = useQuery({
    queryKey: ['wallet-payments', publisherId],
    enabled: Boolean(publisherId),
    refetchInterval: 60000,
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetchAllPaginatedItems<WalltetPayment>(async pageNumber => {
        const response = await axios.get<WalltetPaymentsResponse>(
          `${reatailmediaApi}/payment?${formatSearchString({
            page: pageNumber,
            quantity: 50,
            publisher_id: publisherId
          })}`
        )
        return response.data
      })
  })

  const [AlertOnError] = useAlert(error)

  const filtered = useMemo(
    () =>
      filterWalletPayments(data || [], {
        status: status || null,
        method: method || null,
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD')
      }),
    [data, endDate, method, startDate, status]
  )

  useEffect(() => {
    dispatch(setPageIndustryListPayments(1))
  }, [dispatch, endDate, method, startDate, status])

  const pageItems = filtered.slice((page - 1) * perPage, page * perPage)

  const handlePagination = useCallback(
    (nextPage: number) => {
      dispatch(setPageIndustryListPayments(nextPage))
    },
    [dispatch]
  )

  const handleItemsPerPageChange = useCallback(
    ({ value }: { value: number }) => {
      dispatch(setPerPageIndustryListPayments(value))
    },
    [dispatch]
  )

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <WalletShorelineFilter
          label={t('rm:wallet.movimentations.filter.status')}
          value={status}
          onChange={setStatus}
          options={[
            {
              value: '',
              label: t('rm:wallet.movimentations.filter.status.all')
            },
            ...PAYMENT_STATUS_OPTIONS.map(option => ({
              value: option,
              label: transactionStatus[option]?.label || option
            }))
          ]}
        />
        <WalletShorelineFilter
          label={t('rm:wallet.movimentations.filter.paymentMethod')}
          value={method}
          onChange={setMethod}
          options={[
            {
              value: '',
              label: t('rm:wallet.movimentations.filter.paymentMethod.all')
            },
            ...PAYMENT_METHOD_OPTIONS.map(option => ({
              value: option,
              label: t(`rm:paymentMethod.${option}`)
            }))
          ]}
        />
        <DatepickerHeader />
      </div>

      <Loading status={!data && isFetching}>
        {t('common:actions.loadingData')}...
      </Loading>

      <LoadingPopUp isActive={isFetching} />

      <AlertOnError />

      {!!data && filtered.length === 0 && (
        <EmptyPage
          title={t('common:table.empty')}
          subtitle={t('common:table.emptyFiltered')}
        />
      )}

      {filtered.length > 0 && (
        <>
          <div className={activityStyles.tableScroll}>
            <Table
              schema={schema({ walletId: id, publisherId })}
              data={pageItems}
              total={filtered.length}
              sideBorder
            />
          </div>

          <footer>
            <ListPagination
              total={filtered.length}
              label={t(
                `rm:wallet.movimentations.pagination.label.${filtered.length > 1 ? 'plural' : 'singular'}`
              )}
              currentPage={page}
              itemsPerPage={perPage}
              menuPlacement="top"
              onClickPagination={handlePagination}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </footer>
        </>
      )}
    </>
  )
}
